import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import MemberDashboard from "@/pages/MemberDashboard";
import Jobs from "@/pages/Jobs";
import JobNew from "@/pages/JobNew";
import JobDetail from "@/pages/JobDetail";
import Board from "@/pages/Board";
import Members from "@/pages/Members";
import MemberDetail from "@/pages/MemberDetail";
import Membership from "@/pages/Membership";
import Payments from "@/pages/Payments";
import Financials from "@/pages/Financials";
import Documents from "@/pages/Documents";
import Workshop from "@/pages/Workshop";
import ContentHub from "@/pages/ContentHub";
import Notifications from "@/pages/Notifications";
import ChangePassword from "@/pages/ChangePassword";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0D0D0E]">
      <div className="font-serif text-2xl brass-text animate-pulse">3B</div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password && location.pathname !== "/change-password") return <Navigate to="/change-password" replace />;
  return <Layout>{children}</Layout>;
}

function AuthedBare({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password && location.pathname !== "/change-password") return <Navigate to="/change-password" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  return user?.role === "admin" ? <AdminDashboard /> : <MemberDashboard />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<AuthedBare><ChangePassword /></AuthedBare>} />
          <Route path="/" element={<Protected><Home /></Protected>} />
          <Route path="/board" element={<AdminRoute><Board /></AdminRoute>} />
          <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
          <Route path="/jobs/new" element={<Protected><JobNew /></Protected>} />
          <Route path="/jobs/:id" element={<Protected><JobDetail /></Protected>} />
          <Route path="/members" element={<AdminRoute><Members /></AdminRoute>} />
          <Route path="/members/:id" element={<AdminRoute><MemberDetail /></AdminRoute>} />
          <Route path="/membership" element={<Protected><Membership /></Protected>} />
          <Route path="/payments" element={<AdminRoute><Payments /></AdminRoute>} />
          <Route path="/financials" element={<AdminRoute><Financials /></AdminRoute>} />
          <Route path="/documents" element={<Protected><Documents /></Protected>} />
          <Route path="/workshop" element={<Protected><Workshop /></Protected>} />
          <Route path="/content" element={<Protected><ContentHub /></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
          <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </AuthProvider>
  );
}

export default App;
